/**
 * @changelog
 *
 * 2026-03-11 (0.1.0) (jhoward)
 *   - Add App\Helpers\peppermillCustomFetchProxy() to wrap peppermillCustomFetch with advanced 401/auth handling.
 *   - Handles reauthentication, two-factor, unauthenticated, and generic 401 responses with store and router integration.
 *
 * 2026-03-13 (0.1.1) (jhoward)
 *   - Add debug logging for route.
 *   - Handle 403 error for unverified email by redirecting to email.verify route.
 *
 * 2026-03-13 (0.1.2) (jhoward)
 *   - Use appStore.currentRoute instead of router.currentRoute.value.
 *   - Add null-safe checks for route properties.
 *   - Improve 2FA and unauthenticated redirect logic.
 *
 * 2026-03-17 (0.1.3) (jhoward)
 *   - Update to handle both email and phone verification redirects based on error message.
 *
 * 2026-03-24 (0.1.4) (jhoward)
 *   - Normalize route URLs using getRouteWithFixedUrl.
 *   - Comment out console logs for cleaner output.
 *
 * 2026-03-30 (0.1.5) (jhoward)
 *   - Comment out debug console.log in 401 response handling.
 *
 * 2026-04-09 (0.1.6) (jhoward)
 *   - Send errors to gtag for analytics.
 *   - Handle account_reverify_required by redirecting to account.reverify route.
 *
 * 2026-09-10 (0.1.7) (jhoward)
 *   - Test route.meta.authRequired rather than route.meta.requiresAuth on an 'Unauthenticated.' response. No route
 *     sets requiresAuth, so the redirect to login never fired for an in-page fetch: a guest whose session had
 *     expired got a resolved 401 and a screen that silently stopped updating. RouteMeta is now declared in
 *     src/shims-app.d.ts so the next misspelling does not compile.
 *
 * 2026-09-10 (0.2.0) (jhoward)
 *   - Answer on every path. Five branches called router.push() and then fell out of the promise executor without
 *     resolving, so anything awaiting them never resumed and their finally blocks never ran - spinners stayed up
 *     behind the navigation.
 *   - Drop the `new Promise(async …)` wrapper. The function is already async, so returning and throwing directly
 *     does the same job without the executor swallowing anything the async body throws after its first await.
 *   - Move the analytics reporting into reportErrorsToAnalytics() and stop it being able to fail the call.
 *
 * 2026-10-09 (0.2.1) (jhoward)
 *   - Queue every call that hits REAUTHENTICATION_REQUIRED while the dialog is open and release them all from the one
 *     store callback. Only the last call used to be answered; the rest stayed pending forever.
 *   - Export releasePendingReauthentication() so views that install their own callback can release the queue.
 *
 * 2026-10-10 (0.3.0) (jhoward)
 *   - Handle a 403 carrying PASSWORD_RESET_REQUIRED: say so, and go to the password section of Sign-in & Security.
 *
 * 2026-10-15 (0.3.1) (jhoward)
 *   - On 'Unauthenticated.' drop the patron's account data as well as the user (appStore.resetAccountState()), so
 *     whoever signs in next on this tab is not shown it.
 *
 * 2026-10-28 (0.4.0) (jhoward)
 *   - A 503 carrying PROPERTY_SHUTOFF raises the store's propertyShutoff flag, so App.vue replaces the page with the
 *     maintenance notice, and returns without the sign-in handling. A signed-in guest on a property that has just been
 *     shut off is shown the notice rather than sent to the login page.
 */

import type { FetchResponse, PeppermillCustomFetchData, PeppermillCustomFetchObject } from '@framework/defs/framework';
import type { FetchOptions } from '@framework/defs/helper';
import { peppermillCustomFetch } from '@framework/helpers';
import type { FetchResponseStatus, PeppermillFetchStoreActions } from '@framework/helpers/peppermillCustomFetch';
import type { Store } from 'pinia';
import getRouteWithFixedUrl from '@/helpers/getRouteWithFixedUrl';
import router from '@/router';
import useAppStore from '@/store';
import type { AppStoreState } from '@/store';

const pendingReauthentication: Array<(wasSatisfied: boolean) => void> = [];

/**
 * Settle every call waiting on the reauthentication dialog: replay them if the guest reauthenticated, otherwise
 * hand each its original 401. The queue is emptied first so a replay that 401s again queues afresh.
 */
export function releasePendingReauthentication(wasSatisfied: boolean = true): void {
  pendingReauthentication.splice(0).forEach((release) => release(wasSatisfied));
}

export default async function peppermillCustomFetchProxy<T extends FetchResponseStatus = FetchResponse>(
  data: PeppermillCustomFetchData | FormData | PeppermillCustomFetchObject,
  options: FetchOptions = {},
  appStore = useAppStore() as Store<'app', AppStoreState, {}, PeppermillFetchStoreActions>,
): Promise<T> {
  if (options.route?.url) {
    options.route = getRouteWithFixedUrl(options.route);
  }

  const response = await peppermillCustomFetch<T>(data, options, appStore);
  const route = appStore.currentRoute;

  reportErrorsToAnalytics(response, route?.path);

  if (503 === response.status && Array.isArray(response.errors) && response.errors.includes('PROPERTY_SHUTOFF')) {
    useAppStore().propertyShutoff = true;

    return response;
  }

  if (response.status === 401) {
    if (Array.isArray(response.errors) && response.errors?.includes('REAUTHENTICATION_REQUIRED')) {
      /*
       * The one branch that legitimately does not answer yet: the dialog is opened and the original call is
       * replayed once the guest proves it is still them. If they dismiss it instead, the callback is dropped and
       * this promise settles as a 401 rather than hanging - see ReauthenticateDialog.
       */
      return await new Promise<T>((resolve) => {
        /*
         * The store has a single callback slot, so every call that hits this 401 while the dialog is open queues
         * here and one callback releases them all. Assigning a fresh closure per call used to overwrite the
         * previous one and leave those promises pending for the life of the page.
         */
        pendingReauthentication.push((wasSatisfied) => {
          resolve(wasSatisfied ? peppermillCustomFetchProxy<T>(data, options, appStore) : response);
        });
        appStore.state.reauthenticationRequired = true;
        appStore.state.reauthenticationCallback = releasePendingReauthentication;
      });
    }

    if (response.message === 'Insufficient authentication.') {
      appStore.state.twoFactorAuthRequired = true;

      if (route?.meta.twoFactorAuthRequired && response.data?.redirect && route.path !== response.data.redirect) {
        router.push({ path: <string>response.data.redirect });
      } else if (route?.meta.twoFactorAuthRequired && route?.name !== 'login.two-factor.auth') {
        router.push({ name: 'login.two-factor.auth', query: { redirect: route?.fullPath } });
      }

      return response;
    }

    if (response.message === 'Unauthenticated.') {
      appStore.state.twoFactorAuthRequired = true;
      appStore.state.user = null;
      useAppStore().resetAccountState();

      if (route?.meta.authRequired) {
        router.push({ name: 'login', query: { redirect: route?.fullPath } });
      }

      return response;
    }

    return <T>{
      status: 401,
      errors: response.errors ?? [],
      message: response.message ?? 'Unauthorized',
      data: response.data ?? {},
    };
  }

  if (
    403 === response.status &&
    (response.message === 'Your email address is not verified.' ||
      response.message === 'Your phone number is not verified.')
  ) {
    const method = response.message === 'Your email address is not verified.' ? 'email' : 'sms';
    router.push({ name: 'verification.notice', params: { method }, query: { redirect: route?.fullPath } });

    return response;
  }

  if (response.data?.account_reverify_required) {
    //INVALID_ACCESS_TOKEN, account needs to reverify
    router.push({ name: 'account.reverify', query: { intended_url: route?.fullPath } });

    return response;
  }

  //An administrator requires a new password before this action (booking, the admin tools). Reading is still allowed.
  if (403 === response.status && Array.isArray(response.errors) && response.errors.includes('PASSWORD_RESET_REQUIRED')) {
    useAppStore().setNewSnack(15000, response.message || 'Please change your password before continuing.', 'warning');
    if ('account.security' !== route?.name) {
      router.push({ name: 'account.security', hash: '#password' });
    }

    return response;
  }

  return response;
}

/**
 * Report whatever the response complained about, in either of the two shapes the API uses: a flat list of
 * messages, or a map of field name to messages.
 *
 * window.gtag is defined by resources/views/app.blade.php whether or not analytics is configured, so this is safe
 * to call unconditionally - but it is wrapped anyway. Nothing about reporting an error should be able to turn a
 * response the caller could have handled into a rejection, which is what it did when the shim was missing.
 */
function reportErrorsToAnalytics(response: FetchResponseStatus, pagePath: string | undefined): void {
  if (!response.errors) {
    return;
  }

  const send = (message: string, key: string) => {
    window.gtag('event', 'error', {
      error_type: 'fetch error',
      error_msg: message,
      error_key: key,
      page_path: pagePath || '(not set)',
    });
  };

  try {
    if (Array.isArray(response.errors)) {
      response.errors.forEach((error) => send(error, '(not set)'));
    } else {
      for (const key in response.errors) {
        response.errors[key].forEach((error) => send(error, key));
      }
    }
  } catch (error) {
    console.error('could not report fetch errors to analytics:', error);
  }
}
